import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardBody, Collapse, Row, Col, Badge } from 'reactstrap';
import { format, parseISO } from 'date-fns';
import useRoles from '~/hooks/useRoles';
import { logPiiRevealOnce } from '~/services/audit';

export const PickupValidationCard = ({ cart }) => {
  const [expanded, setExpanded] = useState(window.innerWidth > 768);
  const [revealAudit, setRevealAudit] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  const { isSupport } = useRoles();

  const validation = cart?.delivery?.pickup_validation;

  const handleImageClose = useCallback(() => {
    setImageModalOpen(false);
  }, []);

  useEffect(() => {
    if (!imageModalOpen) return undefined;
    const onKeyDown = e => {
      if (e.key === 'Escape') setImageModalOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [imageModalOpen]);

  const handleToggleReveal = useCallback(() => {
    setRevealAudit(prev => {
      const next = !prev;
      if (next) {
        logPiiRevealOnce({
          referId: cart?.id,
          referModel: 'App/Cart',
          fields: [
            'pickup_attempts_history',
            'pickup_forced_release_reason',
            'pickup_courier_snapshot',
            'pickup_photo',
          ],
          scope: 'pickup_validation_card',
        });
      }
      return next;
    });
  }, [cart]);

  const summary = useMemo(() => {
    if (!validation) return null;

    // Flags que se sobrepõem ao status (mas só fazem sentido em estados terminais/pós-validação)
    if (validation.skipped_validation) {
      return { label: 'Sem validação', className: 'af-badge-red' };
    }
    if (validation.forced_release?.forced) {
      return { label: 'Liberação forçada', className: 'af-badge-orange' };
    }
    if (validation.support_released) {
      return { label: 'Liberado pelo suporte', className: 'af-badge-orange' };
    }

    switch (validation.status) {
      case 'pending':
        return { label: 'Pendente', className: 'af-badge-orange' };
      case 'failed':
        return { label: 'Falha na tentativa', className: 'af-badge-red' };
      case 'blocked':
        return {
          label: 'Bloqueada — aguardando suporte',
          className: 'af-badge-red',
        };
      case 'awaiting_dispatch':
        return { label: 'Aguardando entregador', className: 'af-badge-purple' };
      case 'dispatched':
        return { label: 'Coleta acionada', className: 'af-badge-purple' };
      case 'released':
        return { label: 'Validado', className: 'af-badge-green' };
      case 'canceled':
        return { label: 'Cancelada', className: 'af-badge-gray' };
      default:
        return { label: validation.status, className: 'af-badge-purple' };
    }
  }, [validation]);

  if (!isSupport || !validation) return null;

  if (cart?.delivery?.status === 'SEARCHING') return null;

  const releasedAtText = validation.released_at
    ? format(parseISO(validation.released_at), 'dd/MM/yyyy HH:mm')
    : '-';

  const dispatchedAtText = validation.dispatched_at
    ? format(parseISO(validation.dispatched_at), 'dd/MM/yyyy HH:mm')
    : '-';

  const operatorName = validation.released_by_user?.name;
  const history = Array.isArray(validation.attempts_history)
    ? validation.attempts_history
    : [];
  /*
  const formatAttemptScore = score => {
    if (score === null || score === undefined) return '-';
    return Number(score).toFixed(2);
  };
  */

  const renderPlateMatchCell = plateMatch => {
    if (plateMatch === null) return 'n/a';
    return plateMatch ? '✓' : '✗';
  };

  return (
    <Card className="new-layout card">
      <CardBody className="new-layout card-body px-3 py-3">
        <Row className="w-100 mx-0">
          <Col className="w-100 px-0">
            <div
              className="w-100 font-size-16 font-weight-600 d-flex align-items-center cursor-pointer"
              onClick={() => setExpanded(v => !v)}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') setExpanded(v => !v);
              }}
            >
              <span className="ml-0">Validação de Coleta</span>
              <div className="ml-auto d-flex align-items-center">
                <button
                  type="button"
                  className="p-0 border-0 bg-transparent d-flex align-items-center text-gray-600 reveal-btn"
                  onClick={e => {
                    e.stopPropagation();
                    handleToggleReveal();
                  }}
                  aria-pressed={revealAudit}
                >
                  <i
                    className={`las la-${
                      revealAudit ? 'eye-slash' : 'eye'
                    } font-size-18`}
                  />
                  <span className="ml-1 font-size-12">
                    {revealAudit ? 'Ocultar dados' : 'Revelar dados'}
                  </span>
                </button>
                <button
                  type="button"
                  className="btn btn-link p-0 ml-3 text-gray-600"
                  onClick={e => {
                    e.stopPropagation();
                    setExpanded(v => !v);
                  }}
                  aria-label={expanded ? 'Recolher' : 'Expandir'}
                >
                  <i
                    className={`las la-${
                      expanded ? 'angle-up' : 'angle-right'
                    } font-size-20`}
                  />
                </button>
              </div>
            </div>
          </Col>
        </Row>

        <Collapse isOpen={expanded}>
          <Row className="w-100 mx-0 mt-3 mb-lg-4">
            <Col lg={4} className="px-0 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Resultado
              </div>
              {summary && (
                <Badge
                  pill
                  className={`px-3 py-2 af-font-size ${summary.className}`}
                  style={{
                    whiteSpace: 'normal',
                    display: 'inline-block',
                    maxWidth: '100%',
                    lineHeight: 1.2,
                  }}
                >
                  {summary.label}
                </Badge>
              )}
            </Col>
            <Col lg={4} className="px-0 pl-lg-2 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Tentativas
              </div>
              <div className="font-size-14 font-weight-400 text-gray-700">
                {validation.attempts_count ?? 0}
              </div>
            </Col>
            <Col lg={4} className="px-0 pl-lg-2 mb-2 mb-lg-0">
              <div className="font-size-14 font-weight-600 text-gray-600">
                Tipo de coleta
              </div>
              <div className="font-size-14 font-weight-400 text-gray-700">
                {validation.pickup_code?.type === 'qrcode' && 'QR Code'}
                {validation.pickup_code?.type === 'pin' && 'PIN'}
                {!validation.pickup_code?.type && 'Sem código'}
              </div>
            </Col>
          </Row>

          {(() => {
            const isTerminal =
              validation.status === 'released' ||
              validation.status === 'canceled';
            const hasReleaseData =
              !!validation.released_at ||
              !!validation.released_by_user ||
              !!operatorName;

            if (!isTerminal && !hasReleaseData) {
              return null;
            }

            const wasAutoClosed = !operatorName && !!validation.released_at;
            const isCanceled = validation.status === 'canceled';
            const labelPrefix = isCanceled ? 'Encerrado' : 'Liberado';

            return (
              <Row className="w-100 mx-0 mb-lg-4">
                <Col lg={8} className="px-0 mb-2 mb-lg-0">
                  <div className="font-size-14 font-weight-600 text-gray-600">
                    {labelPrefix} por
                  </div>
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    {operatorName || (
                      <em className="text-muted">
                        {wasAutoClosed
                          ? 'Encerrado automaticamente após entrega'
                          : '—'}
                      </em>
                    )}
                  </div>
                </Col>
                <Col lg={4} className="px-0 pl-lg-2 mb-2 mb-lg-0">
                  <div className="font-size-14 font-weight-600 text-gray-600">
                    {labelPrefix} em
                  </div>
                  <div className="font-size-14 font-weight-400 text-gray-700">
                    {releasedAtText}
                  </div>
                </Col>
              </Row>
            );
          })()}

          {revealAudit && (
            <>
              <hr />

              <Row className="w-100 mx-0 mb-3">
                <Col xs={12} className="px-0">
                  <div className="font-size-14 font-weight-600 text-gray-800 mb-2">
                    Dados do Entregador (Via parceiro)
                  </div>
                </Col>
                <Col lg={4} className="px-0 mb-2 mb-lg-0">
                  <div className="font-size-12 font-weight-600 text-gray-600">
                    Nome
                  </div>
                  <div className="font-size-14 text-gray-700">
                    {validation.courier?.name || '-'}
                  </div>
                </Col>
                <Col lg={4} className="px-0 pl-lg-2 mb-2 mb-lg-0">
                  <div className="font-size-12 font-weight-600 text-gray-600">
                    Placa
                  </div>
                  <div className="font-size-14 text-gray-700">
                    {validation.courier?.plate || '-'}
                  </div>
                </Col>
                <Col lg={4} className="px-0 pl-lg-2 mb-2 mb-lg-0">
                  <div className="font-size-12 font-weight-600 text-gray-600">
                    Telefone
                  </div>
                  <div className="font-size-14 text-gray-700">
                    {validation.courier?.phone || '-'}
                  </div>
                </Col>
              </Row>

              {validation.forced_release?.forced && (
                <Row className="w-100 mx-0 mb-3">
                  <Col xs={12} className="px-0 mb-2">
                    <div className="font-size-14 font-weight-600 text-gray-800">
                      Liberação forçada
                    </div>
                  </Col>
                  <Col lg={6} className="px-0 mb-2 mb-lg-0">
                    <div className="font-size-12 font-weight-600 text-gray-600">
                      Forçada em
                    </div>
                    <div className="font-size-14 text-gray-700">
                      {validation.forced_release?.at
                        ? format(
                            parseISO(validation.forced_release.at),
                            'dd/MM/yyyy HH:mm'
                          )
                        : '-'}
                    </div>
                  </Col>
                  <Col xs={12} className="px-0 mt-2">
                    <div className="font-size-12 font-weight-600 text-gray-600">
                      Justificativa
                    </div>
                    <div
                      className="font-size-14 text-gray-700"
                      style={{ whiteSpace: 'pre-wrap' }}
                    >
                      {validation.forced_release?.reason || '-'}
                    </div>
                  </Col>
                </Row>
              )}

              {history.length > 0 && (
                <Row className="w-100 mx-0 mb-3">
                  <Col xs={12} className="px-0 mb-2">
                    <div className="font-size-14 font-weight-600 text-gray-800">
                      Histórico de Tentativas
                    </div>
                  </Col>
                  <Col xs={12} className="px-0">
                    <div
                      className="table-responsive"
                      style={{ overflowX: 'auto' }}
                    >
                      <table
                        className="table table-sm font-size-12 mb-0"
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Nome</th>
                            <th>Placa</th>
                            <th>Match N</th>
                            {/* <th>Score</th> */}
                            <th>Match P</th>
                            <th>Resultado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {history.map(attempt => (
                            <tr key={`${attempt.attempt}-${attempt.at}`}>
                              <td>{attempt.attempt}</td>
                              <td>{attempt.provided_name || '-'}</td>
                              <td>{attempt.provided_plate || '-'}</td>
                              <td>{attempt.name_match === true ? '✓' : '✗'}</td>
                              {/* <td>{formatAttemptScore(attempt.name_match_score)}</td> */}
                              <td>
                                {renderPlateMatchCell(attempt.plate_match)}
                              </td>
                              <td>
                                <Badge
                                  pill
                                  className={`px-2 ${
                                    attempt.result === 'success'
                                      ? 'af-badge-green'
                                      : 'af-badge-red'
                                  }`}
                                >
                                  {attempt.result === 'success'
                                    ? 'Aprovado'
                                    : 'Falhou'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Col>
                </Row>
              )}

              {validation.pickup_photo_url && (
                <Row className="w-100 mx-0 mb-3">
                  <Col xs={12} className="px-0 mb-2">
                    <div className="font-size-14 font-weight-600 text-gray-800">
                      Foto da retirada
                    </div>
                  </Col>
                  <Col xs={12} className="px-0">
                    <button
                      type="button"
                      onClick={() => setImageModalOpen(true)}
                      style={{
                        background: 'none',
                        border: 0,
                        padding: 0,
                        cursor: 'zoom-in',
                      }}
                    >
                      <img
                        src={validation.pickup_photo_url}
                        alt="Foto da retirada"
                        style={{
                          width: 80,
                          height: 80,
                          objectFit: 'cover',
                          borderRadius: 6,
                          border: '1px solid #e2e2e2',
                        }}
                      />
                    </button>
                    <div className="font-size-12 text-muted mt-1">
                      Clique para ampliar.
                    </div>
                  </Col>
                </Row>
              )}

              <Row className="w-100 mx-0 mb-2">
                <Col lg={6} className="px-0 mb-2 mb-lg-0">
                  <div className="font-size-12 font-weight-600 text-gray-600">
                    Entregador acionou em
                  </div>
                  <div className="font-size-14 text-gray-700">
                    {dispatchedAtText}
                  </div>
                </Col>
                <Col lg={6} className="px-0 pl-lg-2 mb-2 mb-lg-0">
                  <div className="font-size-12 font-weight-600 text-gray-600">
                    Sessão iniciada em
                  </div>
                  <div className="font-size-14 text-gray-700">
                    {validation.created_at
                      ? format(
                          parseISO(validation.created_at),
                          'dd/MM/yyyy HH:mm'
                        )
                      : '-'}
                  </div>
                </Col>
              </Row>

              {validation.skipped_validation && (
                <Row className="w-100 mx-0 mt-3">
                  <Col xs={12} className="px-0">
                    <div
                      className="rounded px-3 py-2 af-badge-red"
                      style={{ lineHeight: 1.4 }}
                    >
                      <strong>OBS:</strong> esta entrega foi concluída sem que a
                      validação de coleta fosse efetivamente completada pelo
                      operador.
                    </div>
                  </Col>
                </Row>
              )}
            </>
          )}
        </Collapse>

        {imageModalOpen && validation.pickup_photo_url && (
          <div
            role="button"
            tabIndex={0}
            aria-label="Fechar visualização"
            onClick={e => {
              if (e.target === e.currentTarget) handleImageClose();
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') handleImageClose();
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.85)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: 20,
              cursor: 'pointer',
              border: 'none',
            }}
          >
            <button
              type="button"
              onClick={handleImageClose}
              aria-label="Fechar"
              style={{
                position: 'absolute',
                top: 20,
                right: 20,
                background: 'rgba(255, 255, 255, 0.15)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: 40,
                height: 40,
                fontSize: 24,
                lineHeight: 1,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ×
            </button>
            <img
              src={validation.pickup_photo_url}
              alt="Foto da retirada (ampliada)"
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: 8,
                cursor: 'default',
                boxShadow: '0 4px 30px rgba(0, 0, 0, 0.5)',
              }}
            />
          </div>
        )}
      </CardBody>
    </Card>
  );
};
